/**
 * Consumer in-app messaging threads API client (plan 289, task:consumer-thread-surfaces).
 *
 * Wire types mirror `backend/internal/api/threads_common.go` +
 * `consumer_threads.go` exactly. All paths are prefixed with `VITE_API_URL`
 * via `api` (worktree relative-fetch rule — relative fetch() 502s in
 * worktrees). See docs/superpowers specs 2026-07-12 marketplace M8
 * post-connection design, §2/§3 (D4/D7).
 */
import { api, ApiError } from "./api";

// ── Wire types ───────────────────────────────────────────────────────────────

export type ThreadSenderRole = "consumer" | "pro" | "system";
export type ThreadMessageKind = "message" | "intro" | "system_event";
export type ThreadMessageSource = "in_app";

/** Message transport channel (messaging-inbox IA north-star §0): v1 rows
 *  are always `in_app`; `email`/`sms` arrive additively on the same model. */
export type ThreadChannel = "in_app" | "email" | "sms";

/** One row from GET /api/consumer/threads (conversation read model). */
export interface ConsumerThread {
  id: string;
  profile_id: string;
  pro_user_id: string;
  consumer_last_read_at?: string;
  pro_last_read_at?: string;
  /** Denormalized marker only — display hint, never the lock authority (D4). */
  locked_at?: string;
  created_at: string;
  /** The pro's display identity — best-effort server enrichment; absent when
   *  the identity seam couldn't resolve a name (render a generic label). */
  counterparty?: { display_name: string; role: string; photo_url?: string };
  /** Newest message summary: ≤140-char preview, never the full body. */
  last_message?: {
    preview: string;
    sender_role: ThreadSenderRole;
    kind: ThreadMessageKind;
    channel: ThreadChannel;
    created_at: string;
  };
  /** Messages from the other party newer than the caller's read watermark. */
  unread_count: number;
  /**
   * Relationship-pipeline stage (D8 rev 4, decision 281): projected from
   * `crm_contacts.stage` onto the thread ONLY when a plan-291 server change
   * lands. Optional and absent on every real thread today — the header
   * chip must render nothing when this is undefined (D21). Do NOT add this
   * field to any backend DTO from this task (plan 291 owns that surface);
   * this type only declares the OPTIONAL wire shape the FE reads.
   */
  relationship_stage?: string;
}

export interface ListThreadsResponse {
  threads: ConsumerThread[];
  /** Conversations with ≥1 unread message — the header-badge total. */
  unread_count: number;
}

/** One row from GET /api/consumer/threads/:id. */
export interface ThreadMessage {
  id: string;
  thread_id: string;
  client_msg_id: string;
  sender_role: ThreadSenderRole;
  sender_user_id?: string;
  body: string;
  kind: ThreadMessageKind;
  source: ThreadMessageSource;
  created_at: string;
}

/**
 * One frozen incentive line item on the M8 hub-bindings pinned card (task
 * 1268). Value is the item's own magnitude — a rebate PERCENTAGE for
 * `commission_rebate`, a dollar figure for the cash-lane types — never a
 * marketplace fee.
 */
export interface PinnedOfferItem {
  type: string;
  value?: number;
}

/**
 * The frozen accepted-offer/standing-offer snapshot, present only on a
 * marketplace-connected thread's GET response (D14/D21). Absent (`undefined`)
 * for an invited/non-marketplace thread — render NO card, never an empty one.
 */
export interface PinnedOffer {
  rebate?: PinnedOfferItem[];
  cash?: PinnedOfferItem[];
  perks?: PinnedOfferItem[];
  commission_pct?: number;
}

export interface ListMessagesResponse {
  messages: ThreadMessage[];
  next_cursor?: string;
  /** M8 hub-bindings pinned-incentive card (task 1268, D14/D21). */
  pinned_offer?: PinnedOffer;
  /** Derived at read time from write-grant absence (D4) — read-only render. */
  locked?: boolean;
  /**
   * M11-B1 (plan 324, D20): the lock's CAUSE is a safety pair block, so the
   * neutral "under review" notice renders instead of the "no longer active"
   * one. `frozen` implies `locked`. Says nothing about who reported whom —
   * there is no wire field anywhere that does.
   */
  frozen?: boolean;
}

export interface PostMessageResponse {
  message: ThreadMessage;
  /** true when the server matched an existing client_msg_id and returned it
   *  unchanged rather than creating a new row (send idempotency, D4). */
  replayed: boolean;
}

/**
 * GET /api/consumer/threads/pair/:proUserId — the pair-addressed lookup
 * backing the "start the conversation" pane. `found: false` (with 200) means
 * the pair is authorized but no thread row exists yet — render an empty
 * composer, not an error. A pro the caller was never connected to 404s.
 */
export interface ThreadPairState {
  found: boolean;
  thread_id?: string;
  counterparty?: { display_name: string; role: string; photo_url?: string };
  /** Derived at read time from write-grant absence (D4) — read-only render. */
  locked: boolean;
  /** The lock's cause is a safety pair block (M11-B1) — neutral notice. */
  frozen: boolean;
}

// ── Errors ───────────────────────────────────────────────────────────────────

/** True when the server rejected a send because the thread has no active
 * grant (link ended / never existed) — composer should render read-only. */
export function isThreadLockedError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409;
}

/** True when the server rejected a send for exceeding the burst/hourly rate
 * limit (§1 D4: 5/min, 60/hour per sender per thread). */
export function isThreadRateLimitedError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 429;
}

/** True when the server rejected a send for an empty or >4000-char body. */
export function isThreadBodyInvalidError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 400;
}

// ── client_msg_id ────────────────────────────────────────────────────────────

/**
 * Generates a fresh client-side message id for send idempotency. The server
 * treats a duplicate `client_msg_id` as a 200 replay of the existing row
 * (never a second insert) — callers should generate ONE id per composed
 * message and reuse it across retries of that same send, not mint a new id
 * per attempt.
 */
export function newClientMsgID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID (older test runners).
  return `cmid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ── API functions ────────────────────────────────────────────────────────────

/** GET /api/consumer/threads — usually one row. Returns the full response
 *  so callers can also read the badge total (`unread_count`). */
export async function listThreads(
  getToken: () => Promise<string | null>,
): Promise<ListThreadsResponse> {
  const token = (await getToken()) ?? undefined;
  return api.get<ListThreadsResponse>("/api/consumer/threads", token);
}

/** GET /api/consumer/threads/:id — cursor-paged messages, newest first. */
export async function getThreadMessages(
  getToken: () => Promise<string | null>,
  threadId: string,
  opts: { before?: string; limit?: number } = {},
): Promise<ListMessagesResponse> {
  const token = (await getToken()) ?? undefined;
  const params = new URLSearchParams();
  if (opts.before) params.set("before", opts.before);
  if (opts.limit != null) params.set("limit", String(opts.limit));
  const qs = params.size > 0 ? `?${params.toString()}` : "";
  return api.get<ListMessagesResponse>(
    `/api/consumer/threads/${threadId}${qs}`,
    token,
  );
}

/**
 * POST /api/consumer/threads/:id/messages — {client_msg_id, body}.
 * Retry-safe: posting the same `client_msg_id` twice returns 200 with the
 * existing message (`replayed: true`) instead of creating a duplicate.
 */
export async function postThreadMessage(
  getToken: () => Promise<string | null>,
  threadId: string,
  clientMsgId: string,
  body: string,
): Promise<PostMessageResponse> {
  const token = (await getToken()) ?? undefined;
  return api.post<PostMessageResponse>(
    `/api/consumer/threads/${threadId}/messages`,
    { client_msg_id: clientMsgId, body },
    token,
  );
}

/**
 * GET /api/consumer/threads/pair/:proUserId — pair-addressed thread lookup
 * for the Your Team "Message" deep link (`/threads?pro=`). See
 * `ThreadPairState` for the found/locked semantics.
 */
export async function getThreadPair(
  getToken: () => Promise<string | null>,
  proUserId: string,
): Promise<ThreadPairState> {
  const token = (await getToken()) ?? undefined;
  return api.get<ThreadPairState>(
    `/api/consumer/threads/pair/${proUserId}`,
    token,
  );
}

/**
 * POST /api/consumer/threads/pair/:proUserId/messages — the pair-addressed
 * first-contact send (consumer twin of the pro's people/:contactId send).
 * The server resolves-or-creates the thread AFTER authorizing the pair, so
 * this is the one send shape that works before any thread exists. Same
 * client_msg_id replay semantics as postThreadMessage.
 */
export async function postThreadPairMessage(
  getToken: () => Promise<string | null>,
  proUserId: string,
  clientMsgId: string,
  body: string,
): Promise<PostMessageResponse> {
  const token = (await getToken()) ?? undefined;
  return api.post<PostMessageResponse>(
    `/api/consumer/threads/pair/${proUserId}/messages`,
    { client_msg_id: clientMsgId, body },
    token,
  );
}

/**
 * GET /api/consumer/threads/unread-count — the cheap header-badge poll
 * (mirrors /api/consumer/notifications/unread-count). Returns the number of
 * conversations with at least one unread message.
 */
export async function getThreadsUnreadCount(
  getToken: () => Promise<string | null>,
): Promise<number> {
  const token = (await getToken()) ?? undefined;
  const resp = await api.get<{ unread_count: number }>(
    "/api/consumer/threads/unread-count",
    token,
  );
  return resp.unread_count;
}

/** POST /api/consumer/threads/:id/read. */
export async function markThreadRead(
  getToken: () => Promise<string | null>,
  threadId: string,
): Promise<void> {
  const token = (await getToken()) ?? undefined;
  await api.post<{ read: boolean }>(
    `/api/consumer/threads/${threadId}/read`,
    {},
    token,
  );
}
